import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { ArrowLeft, Clock, Users, ChefHat, Printer } from 'lucide-react';

export const RecipeDetail = () => {
  const { id } = useParams();
  const { API, token } = useAuth();
  const navigate = useNavigate();
  const [recipe, setRecipe] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRecipe();
  }, [id]);

  const fetchRecipe = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/recipes/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecipe(response.data);
    } catch (error) {
      console.error('Error fetching recipe:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="text-center py-12"><p className="text-[#A8A29E]">Loading recipe...</p></div>;
  }

  if (!recipe) {
    return (
      <div className="text-center py-12">
        <p className="text-[#57534E]">Recipe not found</p>
        <Button onClick={() => navigate('/recipes')} className="mt-4">Back to Recipes</Button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-8" data-testid="recipe-detail">
      <Button onClick={() => navigate('/recipes')} variant="ghost" className="text-primary hover:text-primary/80" data-testid="back-button">
        <ArrowLeft className="w-5 h-5 mr-2" />Back to Recipes
      </Button>

      <div className="bg-gradient-to-br from-secondary/20 to-primary/10 rounded-xl p-8">
        <div className="flex items-start gap-4 mb-4">
          <ChefHat className="w-12 h-12 text-primary flex-shrink-0" strokeWidth={1.5} />
          <h1 className="text-3xl md:text-4xl font-nunito font-bold text-[#2D2D2D] leading-tight">{recipe.title}</h1>
        </div>
        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex items-center gap-2 text-[#57534E]">
            <Clock className="w-5 h-5" />
            <span>Prep: {recipe.prep_time} min | Cook: {recipe.cook_time} min</span>
          </div>
          <div className="flex items-center gap-2 text-[#57534E]">
            <Users className="w-5 h-5" />
            <span>{recipe.servings} servings</span>
          </div>
          <Button onClick={() => window.print()} variant="ghost" className="text-primary" data-testid="print-button">
            <Printer className="w-5 h-5 mr-2" />Print
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {recipe.diet_type.map(diet => (
          <span key={diet} className="px-4 py-2 bg-secondary text-secondary-foreground rounded-full text-sm font-semibold">{diet}</span>
        ))}
      </div>

      <Card className="bg-white rounded-xl border border-stone-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)] p-8">
        <h2 className="text-2xl font-nunito font-bold text-[#2D2D2D] mb-4">Ingredients</h2>
        <ul className="space-y-2">
          {recipe.ingredients.map((ing, i) => (
            <li key={i} className="flex items-start gap-3 text-[#57534E]">
              <span className="w-2 h-2 rounded-full bg-primary mt-2 flex-shrink-0" />
              <span>{ing}</span>
            </li>
          ))}
        </ul>
      </Card>

      <Card className="bg-white rounded-xl border border-stone-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)] p-8">
        <h2 className="text-2xl font-nunito font-bold text-[#2D2D2D] mb-4">Instructions</h2>
        <ol className="space-y-4">
          {recipe.instructions.map((step, i) => (
            <li key={i} className="flex gap-4">
              <span className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center font-bold flex-shrink-0">{i + 1}</span>
              <p className="text-[#57534E] leading-relaxed pt-1">{step}</p>
            </li>
          ))}
        </ol>
      </Card>

      <Card className="bg-gradient-to-br from-primary/5 to-secondary/10 rounded-xl border border-stone-100 p-8">
        <h2 className="text-xl font-nunito font-bold text-[#2D2D2D] mb-3">Nutritional Benefits</h2>
        <p className="text-[#57534E] leading-relaxed">{recipe.nutritional_benefits}</p>
        {recipe.notes && (
          <div className="mt-4 p-4 bg-white rounded-lg">
            <p className="text-sm font-semibold text-primary mb-2">Important Notes:</p>
            <p className="text-[#57534E] text-sm leading-relaxed">{recipe.notes}</p>
          </div>
        )}
      </Card>

      <div className="flex flex-wrap gap-2">
        {recipe.tags.map(tag => (
          <span key={tag} className="px-3 py-1 bg-white border border-stone-200 rounded-full text-sm text-[#57534E]">#{tag}</span>
        ))}
      </div>
    </div>
  );
};

export default RecipeDetail;
